import { FC, useState } from 'react';
import { DropdownItem, SelectA11y } from '@base-app';

import { useTranslation } from 'react-i18next';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import {
  CollectionLocationSelectId,
  DemographicsBy,
  DemographicsOptionType,
  SampleStatusSelectId,
  SampleTypeSelectId,
} from '../Demographics.types';
import { DemographicsHeaderProps } from './DemographicsHeader.types';
import { StyledHeaderContainer, StyledSelectorsContainer } from './DemographicsHeader.styles';
import { PillId } from '../../CollectionAndSequencing.types';
import { Variants } from '@common/types';
import { useFeatureFlags } from '@/modules/feature-flags/hooks';
import { FeatureFlags } from '@/modules/feature-flags/types';

const DemographicsHeader: FC<DemographicsHeaderProps> = ({ data, handleDataFilterChange, selectedPillId }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isFeatureEnabled } = useFeatureFlags();

  const [collectionLocationFilter, setCollectionLocationFilter] = useState<DropdownItem>({
    id: CollectionLocationSelectId.ALL,
    optionType: DemographicsOptionType.COLLECTION_LOCATION,
    label: t('demographics.all_collections_locations'),
  });
  const [statusFilter, setStatusFilter] = useState<DropdownItem>({
    id: SampleStatusSelectId.ALL,
    optionType: DemographicsOptionType.STATUS,
    label: t('demographics.all_sample_status'),
  });
  const [sampleTypeFilter, setSampleTypeFilter] = useState<DropdownItem>({
    id: SampleTypeSelectId.ALL,
    optionType: DemographicsOptionType.SAMPLE_TYPE,
    label: t('demographics.all_sample_type'),
  });

  const COLECTION_LOCATION_OPTIONS = [
    {
      id: CollectionLocationSelectId.ALL,
      optionType: DemographicsOptionType.COLLECTION_LOCATION,
      label: t('demographics.all_collections_locations'),
    },
    {
      id: CollectionLocationSelectId.IN_PERSON,
      optionType: DemographicsOptionType.COLLECTION_LOCATION,
      label: t('demographics.in_person_collection'),
    },
    {
      id: CollectionLocationSelectId.AT_HOME,
      optionType: DemographicsOptionType.COLLECTION_LOCATION,
      label: t('demographics.at_home_collection'),
    },
  ];

  const SAMPLE_STATUS_OPTIONS = [
    {
      id: SampleStatusSelectId.ALL,
      optionType: DemographicsOptionType.STATUS,
      label: t('demographics.all_sample_status'),
    },
    {
      id: SampleStatusSelectId.IN_SEQUENCING,
      optionType: DemographicsOptionType.STATUS,
      label: t('demographics.in_sequencing'),
    },
    {
      id: SampleStatusSelectId.RECOLLECT,
      optionType: DemographicsOptionType.STATUS,
      label: t('demographics.recollects'),
    },
    {
      id: SampleStatusSelectId.SEQUENCING_COMPLETE,
      optionType: DemographicsOptionType.STATUS,
      label: t('demographics.sequencing_complete'),
    },
    {
      id: SampleStatusSelectId.RESULTS_DELIVERED,
      optionType: DemographicsOptionType.STATUS,
      label: t('demographics.results_delivered'),
    },
  ];

  const SAMPLE_TYPE_OPTIONS = [
    {
      id: SampleTypeSelectId.ALL,
      optionType: DemographicsOptionType.SAMPLE_TYPE,
      label: t('demographics.all_sample_type'),
    },
    {
      id: SampleTypeSelectId.BLOOD_SAMPLES,
      optionType: DemographicsOptionType.SAMPLE_TYPE,
      label: t('demographics.blood_samples'),
    },
    {
      id: SampleTypeSelectId.SALIVA_SAMPLES,
      optionType: DemographicsOptionType.SAMPLE_TYPE,
      label: t('demographics.saliva_samples'),
    },
  ];

  if (isFeatureEnabled(FeatureFlags.BUCCAL_SAMPLE_TYPE)) {
    SAMPLE_TYPE_OPTIONS.push({
      id: SampleTypeSelectId.BUCCAL_SAMPLES,
      optionType: DemographicsOptionType.SAMPLE_TYPE,
      label: t('demographics.buccal_samples'),
    });
  }

  const handleFilterChange = (item: DropdownItem): void => {
    handleDataFilterChange(item);
    if (item.optionType === DemographicsOptionType.COLLECTION_LOCATION) setCollectionLocationFilter(item);
    if (item.optionType === DemographicsOptionType.SAMPLE_TYPE) setSampleTypeFilter(item);
    if (item.optionType === DemographicsOptionType.STATUS) setStatusFilter(item);
  };

  const isDemographicsByEmpty = (demographicsByData: DemographicsBy[]): boolean => {
    return !demographicsByData?.length;
  };

  const noAvailableData: boolean =
    isDemographicsByEmpty(data.byAgeRange) &&
    isDemographicsByEmpty(data.byBiologicalSex) &&
    isDemographicsByEmpty(data.byPreferredLanguage) &&
    isDemographicsByEmpty(data.byRaceEthnicity);

  return (
    <StyledHeaderContainer>
      <StyledSelectorsContainer>
        <SelectA11y
          testId="selector-collection-location"
          disabled={noAvailableData}
          css={{ marginRight: '$4' }}
          defaultValue={CollectionLocationSelectId.ALL}
          value={collectionLocationFilter.id}
          id="selector-collection-location"
          items={COLECTION_LOCATION_OPTIONS}
          onChangeDropdownItem={handleFilterChange}
          variant={Variants.SECONDARY}
        />
        {selectedPillId === PillId.SEQUENCED && (
          <SelectA11y
            testId="selector-sample-status"
            disabled={noAvailableData}
            css={{ marginRight: '$4' }}
            defaultValue={SampleStatusSelectId.ALL}
            value={statusFilter.id}
            id="selector-sample-status"
            items={SAMPLE_STATUS_OPTIONS}
            onChangeDropdownItem={handleFilterChange}
            variant={Variants.SECONDARY}
          />
        )}
        {(selectedPillId === PillId.SEQUENCED || selectedPillId === PillId.RECEIVED) && (
          <SelectA11y
            testId="selector-sample-type"
            disabled={noAvailableData}
            css={{ marginRight: '$4' }}
            defaultValue={SampleTypeSelectId.ALL}
            value={sampleTypeFilter.id}
            id="selector-sample-type"
            items={SAMPLE_TYPE_OPTIONS}
            onChangeDropdownItem={handleFilterChange}
            variant={Variants.SECONDARY}
          />
        )}
      </StyledSelectorsContainer>
    </StyledHeaderContainer>
  );
};

export default DemographicsHeader;
